import { useEffect } from 'react'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import Footer from '../components/Footer'

const INCLUDED = [
  '3 live apps',
  'Unlimited custom domains',
  'Managed databases',
  'OpenDeploy subdomain + SSL',
  'Build logs and deploy history',
  'Agent returns a live URL',
]

const GA_PRICE = '20'
// "Get Plan" sends the visitor to the dashboard with ?checkout=builder — the
// backend plan name for the $20 Builder plan this card advertises. The
// dashboard reads that param on arrival (it survives the SSO round-trip) and
// opens Stripe checkout for that exact plan right away. Naming the plan here
// (rather than letting the dashboard guess) keeps the two in sync. Humans
// handle billing in the dashboard, not on the marketing page.
const DASHBOARD_SUBSCRIBE_URL = 'https://dashboard.opendeploy.dev/?checkout=builder'

const BUILDER_SPECS = ['2 vCPU', '4 GB memory', '20 GB storage', '150 GB bandwidth']

const LLM_PRICING = [
  {
    provider: 'OpenAI',
    model: 'gpt-5-nano',
    input: '$0.05',
    output: '$0.40',
  },
  {
    provider: 'OpenAI',
    model: 'gpt-5-mini',
    input: '$0.25',
    output: '$2.00',
  },
  {
    provider: 'OpenAI',
    model: 'gpt-5',
    input: '$1.25',
    output: '$10.00',
  },
  {
    provider: 'OpenAI',
    model: 'gpt-4o-mini-2024-07-18',
    input: '$0.15',
    output: '$0.60',
  },
  {
    provider: 'OpenAI',
    model: 'o4-mini',
    input: '$1.10',
    output: '$4.40',
  },
  {
    provider: 'Google',
    model: 'gemini-2.5-flash',
    input: '$0.30',
    output: '$2.50',
  },
  {
    provider: 'Google',
    model: 'gemini-2.5-flash-lite',
    input: '$0.10',
    output: '$0.40',
  },
  {
    provider: 'Anthropic',
    model: 'claude-sonnet-4-5',
    input: '$3.00',
    output: '$15.00',
  },
  {
    provider: 'Anthropic',
    model: 'claude-opus-4-5',
    input: '$5.00',
    output: '$25.00',
  },
  {
    provider: 'xAI',
    model: 'grok-4-fast-reasoning',
    input: '$0.20',
    output: '$0.50',
  },
  {
    provider: 'xAI',
    model: 'grok-4',
    input: '$3.00',
    output: '$15.00',
  },
]

const PRICING_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'OpenDeploy',
  description:
    'OpenDeploy pricing includes a $20 per month Builder plan, hosting specs, and AI Hub LLM model pricing examples displayed per 1M input and output tokens.',
  brand: { '@type': 'Brand', name: 'OpenDeploy' },
  offers: [
    {
      '@type': 'Offer',
      name: 'Builder',
      price: '20',
      priceCurrency: 'USD',
      availability: 'https://schema.org/PreOrder',
      url: 'https://opendeploy.dev/pricing',
      description:
        'Builder plan, $20 per month. One flat monthly hosting price. Includes 3 live apps, unlimited custom domains, and managed databases.',
    },
  ],
}

export default function Pricing() {
  useEffect(() => {
    document.body.classList.add('v2-pricing-page')

    return () => {
      document.body.classList.remove('v2-pricing-page')
    }
  }, [])

  return (
    <>
      <SEO
        title="Pricing — OpenDeploy"
        description="OpenDeploy pricing includes a $20 per month Builder plan, hosting specs, and AI Hub LLM pricing shown per 1M input and output tokens."
        path="/pricing"
        jsonLd={PRICING_JSONLD}
      />

      <div className="od-home od-pricing-shell">
        <span className="od-corner od-corner-tl" aria-hidden="true" />
        <span className="od-corner od-corner-tr" aria-hidden="true" />
        <span className="od-corner od-corner-bl" aria-hidden="true" />
        <span className="od-corner od-corner-br" aria-hidden="true" />

        <SiteNav />

        <main className="od-pricing-main">
          <section className="od-pricing-hero" aria-labelledby="pricing-title">
            <div className="od-pricing-hero-text">
              <h1 id="pricing-title">
                <span className="od-pricing-hero-title-main">Simple pricing for agent deployments.</span>
              </h1>
              <p>One hosting plan for production apps, plus AI Hub model pricing displayed the same way as the dashboard.</p>
            </div>
          </section>

          <section className="od-pricing-grid" aria-label="OpenDeploy pricing">
            <article className="od-pricing-card od-pricing-plan">
              <div className="od-pricing-card-head">
                <span>builder plan</span>
                <i aria-hidden="true" />
              </div>
              <div className="od-price-lockup">
                <span className="od-price-amount">${GA_PRICE}</span>
                <span className="od-price-unit">per month</span>
              </div>
              <p>Three apps, unlimited custom domains, and managed databases for production projects.</p>
              <a href={DASHBOARD_SUBSCRIBE_URL} className="od-pricing-cta">
                Get Plan
                <span aria-hidden="true">→</span>
              </a>
            </article>

            <article className="od-pricing-card od-pricing-card-included">
              <div className="od-pricing-card-head">
                <span>included</span>
                <i aria-hidden="true" />
              </div>
              <h2>What's included</h2>
              <ul className="od-pricing-checks">
                {INCLUDED.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="od-database-row" aria-label="Builder plan notes">
                <span>Flat monthly hosting</span>
                <i aria-hidden="true">·</i>
                <span>Usage-based LLM calls billed through AI Hub credits</span>
              </div>
            </article>
          </section>

          <section className="od-llm-pricing" aria-labelledby="llm-pricing-title">
            <div className="od-llm-pricing-head">
              <span>AI Hub models</span>
              <h2 id="llm-pricing-title">LLM</h2>
              <p>
                Model rates from the AI Hub Models section. Prices are shown per 1M input and output tokens.
              </p>
            </div>

            <div className="od-llm-pricing-table-wrap">
              <table className="od-llm-pricing-table">
                <thead>
                  <tr>
                    <th scope="col">Provider</th>
                    <th scope="col">Model</th>
                    <th scope="col">Input / 1M</th>
                    <th scope="col">Output / 1M</th>
                  </tr>
                </thead>
                <tbody>
                  {LLM_PRICING.map((row) => (
                    <tr key={`${row.provider}-${row.model}`}>
                      <td data-label="Provider">{row.provider}</td>
                      <td data-label="Model">
                        <code>{row.model}</code>
                      </td>
                      <td data-label="Input / 1M">{row.input}</td>
                      <td data-label="Output / 1M">{row.output}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="od-llm-pricing-note">
              Exact model availability can change by provider. Open the dashboard for the full live model list.
            </p>
          </section>

          <section className="od-pricing-specs" aria-labelledby="pricing-specs-title">
            <svg className="od-specs-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3 4.5 7.1v9.8L12 21l7.5-4.1V7.1L12 3Z" />
              <path d="m4.8 7.4 7.2 4 7.2-4" />
              <path d="M12 11.4V21" />
            </svg>
            <h2 id="pricing-specs-title">Builder specs:</h2>
            <div className="od-specs-list">
              {BUILDER_SPECS.map((spec, index) => (
                <span key={spec}>
                  {spec}
                  {index < BUILDER_SPECS.length - 1 && <i aria-hidden="true">·</i>}
                </span>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  )
}
